import { PageHeader, SectionTitle } from "@/components/base/ui";
import { services } from "@/mocks/products";

export default function Services() {
  return (
    <div>
      <PageHeader
        title="Our Services"
        subtitle="End-to-end stone solutions for B2B clients. From quarry to construction site, we handle every step with precision."
      />

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionTitle
            label="What We Offer"
            title="Comprehensive Stone Solutions"
            subtitle="Every service is designed to simplify your procurement process and ensure you receive the finest materials on time."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="group bg-white rounded-lg p-6 md:p-8 border border-stone-200 hover:border-stone-300 transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-stone-50 rounded-lg border border-stone-100 group-hover:border-gold-300 transition-colors mb-6">
                  <i className={`${service.icon} text-gold-500 text-2xl`}></i>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-stone-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-stone-500 text-sm font-sans leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionTitle
            label="Our Process"
            title="From Quarry to Your Site"
            subtitle="A transparent, efficient workflow that ensures quality at every stage."
          />
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-[2px] bg-stone-200"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
              {[
                {
                  step: "01",
                  title: "Selection",
                  text: "Browse our catalog or visit our showroom. Our team assists with stone selection, providing samples, photos, and technical specifications.",
                },
                {
                  step: "02",
                  title: "Quotation",
                  text: "Receive a detailed quotation within 24 hours. Pricing includes stone, processing, packaging, shipping, and optional insurance.",
                },
                {
                  step: "03",
                  title: "Production",
                  text: "Your order is processed at the quarry. Slabs are cut to specification, surface-finished, and undergo rigorous quality inspection.",
                },
                {
                  step: "04",
                  title: "Delivery",
                  text: "Containers are loaded and shipped to your nearest port. We handle customs clearance and arrange transport to your facility.",
                },
              ].map((item, idx) => (
                <div key={idx} className="relative flex flex-col items-center text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-stone-900 text-white rounded-full font-serif font-bold text-lg z-10 mb-6">
                    {item.step}
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-stone-500 text-sm font-sans leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Logistics Details */}
      <section className="py-16 md:py-24 bg-stone-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div>
              <span className="text-gold-500 text-xs font-sans uppercase tracking-[0.2em] mb-4 block">
                Logistics
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 leading-tight mb-6">
                Container Shipping & Freight Solutions
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: "Full Container Load (FCL)",
                    text: "20ft and 40ft containers for large orders. Each container is packed with steel A-frames and wooden crates to protect slabs during transit.",
                  },
                  {
                    title: "Less Than Container Load (LCL)",
                    text: "For smaller orders, we consolidate shipments with trusted freight partners. Cost-effective for project-specific quantities.",
                  },
                  {
                    title: "European Port Coverage",
                    text: "We ship to Marseille, Le Havre, Barcelona, Genoa, and Antwerp. From port, we arrange trucking to your warehouse or job site.",
                  },
                  {
                    title: "Documentation & Customs",
                    text: "All export/import paperwork handled: commercial invoices, packing lists, certificates of origin, phytosanitary certificates, and customs declarations.",
                  },
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-gold-500 text-white rounded-md text-sm font-sans font-bold flex-shrink-0 mt-1">
                      {idx + 1}
                    </div>
                    <div>
                      <h4 className="font-serif text-lg font-semibold text-stone-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-stone-500 text-sm font-sans leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Large%20shipping%20containers%20stacked%20at%20industrial%20port%2C%20container%20yard%20with%20cranes%2C%20professional%20logistics%20photography%2C%20warm%20daylight%2C%20industrial%20import%20export%20scene%2C%20clean%20composition%2C%20professional%20maritime%20cargo%20handling%2C%20Mediterranean%20port%20atmosphere%2C%20modern%20shipping%20infrastructure&width=600&height=500&seq=14&orientation=landscape"
                alt="Container shipping logistics"
                className="w-full h-80 md:h-96 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Wholesale Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20stone%20warehouse%20interior%20with%20polished%20marble%20slabs%20on%20display%20racks%2C%20luxury%20stone%20showroom%20with%20warm%20lighting%2C%20professional%20architectural%20photography%2C%20clean%20minimal%20space%2C%20beige%20and%20white%20tones%2C%20high-end%20material%20showroom%2C%20organized%20slab%20storage%2C%20professional%20interior%20design&width=600&height=500&seq=15&orientation=landscape"
                alt="Stone showroom"
                className="w-full h-80 md:h-96 object-cover rounded-lg"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-gold-500 text-xs font-sans uppercase tracking-[0.2em] mb-4 block">
                Wholesale
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 leading-tight mb-6">
                B2B Wholesale Supply
              </h2>
              <p className="text-stone-500 text-base font-sans leading-relaxed mb-6">
                We partner with stone fabricators, distributors, and construction companies across Europe. Our wholesale program offers competitive pricing, consistent supply, and dedicated account management.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  "Volume discounts on orders over 100 m²",
                  "Fixed monthly supply contracts available",
                  "Credit terms for established partners",
                  "Priority access to new quarry arrivals",
                  "Co-branded marketing materials on request",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-gold-500 text-sm"></i>
                    </div>
                    <p className="text-stone-600 text-sm font-sans">{item}</p>
                  </div>
                ))}
              </div>
              <a
                href="/quote"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-stone-900 text-white font-sans font-medium text-sm rounded-md hover:bg-stone-800 transition-colors whitespace-nowrap"
              >
                Become a Partner
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-stone-900">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Start Your Stone Project
          </h2>
          <p className="text-stone-400 text-base font-sans max-w-xl mx-auto mb-8 leading-relaxed">
            Whether you need a single container or a long-term supply partnership, we are ready to support your projects.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gold-500 text-white font-sans font-medium text-sm rounded-md hover:bg-gold-600 transition-colors whitespace-nowrap"
          >
            Contact Us
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>
    </div>
  );
}